/**
 *
 *  Organizations/Show/Users/Admins
 *
 */

import React, { useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// GraphQL
import useQuery from 'components/UseQuery'
import { useMutation, gql } from '@apollo/client'

// Components
import DeleteButton from 'components/DeleteButton'
import Modal from 'components/Modal'
import UsersSelect from 'components/Organizations/Show/Users/Select'
import { COLORS } from 'global-styles'
import { REMOVE_USER } from '../Table'

const GET_ORGANIZATION_ADMIN_USER = gql`
	query organization($id: ID) {
		organization(id: $id) {
			id
			admin_users {
				id
				full_name
				company_name
				admin_organizations {
					id
					name
				}
			}
		}
	}
`

const ADD_USER = gql`
	mutation addUserToOrganization($organization_id: ID!, $user_id: ID!, $user_admin: Boolean) {
		addUserToOrganization(
			input: { organization_id: $organization_id, user_id: $user_id, user_admin: $user_admin }
		) {
			organization {
				id
				users {
					id
					full_name
					first_name
					last_name
					email
				}
			}
			user {
				full_name
			}
			error
			message
		}
	}
`

const UserList = styled.ul`
	margin: 0;
	padding: 0;

	border-top: 1px solid ${COLORS.grey_e};

	& > li {
		padding: 10px 20px;
		border: 1px solid ${COLORS.grey_e};
		border-top: 0;

		display: flex;
		align-items: center;
		justify-content: space-between;

		p {
			margin: 0;
			line-height: 1.2rem;
		}

		&:hover {
			background-color: ${COLORS.lightGrey};
		}
	}
`

const Headers = styled.div`
	display: flex;
	align-items: center;
	white-space: nowrap;
	margin-bottom: 10px;
	font-style: italic;

	&::after {
		content: '';
		flex: 1;
		height: 1px;
		background: ${COLORS.grey_c};
		margin-left: 7px;
	}
`

function AdminUsers({ organization_id, handleClose }) {
	const [error, setError] = useState(false)

	const {
		data: { organization },
	} = useQuery(GET_ORGANIZATION_ADMIN_USER, {
		variables: { id: organization_id },
		channels: [organization_id],
		skip: !organization_id,
		fetchPolicy: 'network-only',
	})

	const [addUsers, { loading }] = useMutation(ADD_USER, {
		onCompleted: ({ addUserToOrganization: { error: _error, message, user } }) => {
			if (_error) {
				setError({ message, user_name: user?.full_name })
			} else {
				setError(false)
			}
		},
	})

	const [removeUser, { loading: removing }] = useMutation(REMOVE_USER, { onCompleted: () => setError(false) })

	return (
		<Modal
			show
			modalDidClose={handleClose}
			style={{
				width: 670,
			}}
		>
			{(loading || removing) && (
				<FontAwesomeIcon
					className="float-right text-primary mt-2"
					icon="spinner"
					spin
					style={{ fontSize: '25px' }}
				/>
			)}
			<h3 className="text-center mb-4">Organization Administrators</h3>

			<p>
				Organization Administrators are typically immediate employees of the Broker/Dealer, RIA, or FMO. When a
				User is added as an Administrator, LeadJig will automatically grant them Shared User Access across all
				Advisor-level Users within the Organization.
			</p>

			<Headers>Your Organization Administrators</Headers>

			{organization?.admin_users.length === 0 ? (
				<div className="alert alert-info">You don't have any administrator Users.</div>
			) : (
				<UserList className="mb-3" style={{ borderTopWidth: '1px' }}>
					{organization?.admin_users?.map((user) => (
						<li key={user.id}>
							<p>
								<a href={`/users/preferences/${user.id}`} target="_blank">
									<strong>{user.full_name}</strong>
								</a>
								<br />
								<small className="text-muted">
									{[user.company_name, user.email].filter((x) => x).join(' - ')}
								</small>
							</p>

							<div className="d-flex align-items-center">
								{user.admin_organizations.length > 1 && (
									<FontAwesomeIcon
										icon="info-circle"
										className="mr-3"
										style={{ color: COLORS.grey_9 }}
										data-tip
										data-for={user.id}
										data-event="click"
									/>
								)}

								<DeleteButton
									deleting={removing}
									onDelete={() => removeUser({ variables: { user_id: user.id, organization_id } })}
								/>
							</div>

							<ReactTooltip id={user.id} place="right" globalEventOff="click" clickable>
								<div className="py-2" style={{ fontSize: '1rem' }}>
									<p>This User is Administrator for the following other Organizations:</p>

									<ul className="mt-2 pl-4">
										{user.admin_organizations.map((org) =>
											org.id !== organization_id ? (
												<li>
													<a href={`/organizations/${org.id}`} target="_blank">
														{org.name}{' '}
													</a>
												</li>
											) : null,
										)}
									</ul>
								</div>
							</ReactTooltip>
						</li>
					))}
				</UserList>
			)}

			<Headers>Select Users to be Organization Administrators</Headers>

			{error && (
				<div className="alert alert-danger">
					<span>
						The User <b>{error.user_name}</b> {error.message}
					</span>
				</div>
			)}

			<UsersSelect
				loading={loading}
				onSelect={(user) =>
					addUsers({
						variables: { organization_id, user_id: user.id, user_admin: true },
					})
				}
				selected_users={organization?.admin_users}
			/>
		</Modal>
	)
}

AdminUsers.propTypes = {
	organization_id: PropTypes.string,
	handleClose: PropTypes.func,
}

export default AdminUsers
